<template>
    <div class="banner-upload">
        <el-form ref="formRef" :model="formData" :rules="formRules" label-position="top" class="banner-form">
            <!-- 背景图（多语言 fileAssetId）——对齐 galaxy：语言 tabs 右上角、按钮"添加背景图"、高度 100px -->
            <el-form-item prop="fileAssetId" required class="file-form-item">
                <template slot="label">
                    <span class="file-label">
                        <span>背景图</span>
                        <span class="file-size">（2288 x 480 px）</span>
                    </span>
                </template>

                <div class="file-hint">（支持 png/jpg/jpeg/bmp/svg）</div>

                <div class="file-lang-tabs">
                    <span v-for="lang in langTabs" :key="lang.value" class="lang-tab"
                        :class="{ active: currentLang === lang.value }" @click="currentLang = lang.value">
                        {{ lang.label }}
                    </span>
                </div>

                <div class="file-area" :class="{ 'file-area-has-content': currentFile }">
                    <el-upload v-if="!currentFile" class="file-upload file-upload-empty" action="" :auto-upload="false"
                        :show-file-list="false" :on-change="handleFileChange" :accept="uploadImageAcceptFormat">
                        <div class="file-empty">
                            <el-button type="primary">添加背景图</el-button>
                        </div>
                    </el-upload>
                    <div v-if="currentFile" class="file-preview-wrapper">
                        <div class="file-preview-container">
                            <el-upload class="file-upload" action="" :auto-upload="false" :show-file-list="false"
                                :on-change="handleFileChange" :accept="uploadImageAcceptFormat">
                                <el-image :src="currentFile.url" :key="`${currentLang}-${currentFile.url || ''}`"
                                    class="file-img" fit="contain" :lazy="true" />
                            </el-upload>
                            <div class="file-remove" @click.stop="removeCurrentFile">
                                <i class="el-icon-close"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </el-form-item>

            <!-- 标题：对齐 galaxy（随当前语言切换，仅显示一个输入框） -->
            <el-form-item label="标题" prop="title" required>
                <el-input v-model="valueByLanguage(formData.title, currentLang).value" placeholder="请输入标题" />
            </el-form-item>

            <!-- 推送配置 / 跳转配置（对齐 galaxy） -->
            <el-form-item label="推送配置">
                <PushConfig ref="pushConfigRef" />
            </el-form-item>

            <el-form-item label="跳转配置">
                <NavConfig ref="navConfigRef" />
            </el-form-item>
        </el-form>

        <div class="footer">
            <el-button @click="$emit('close')">取消</el-button>
            <el-button type="primary" :loading="loading" @click="handleSubmit">
                确认
            </el-button>
        </div>
    </div>
</template>

<script>
import api from "@/api";
import PushConfig from "@/components/PushConfig/index.vue";
import NavConfig from "@/components/NavConfig.vue";
import { getMd5, uploadFileToOssByPath } from "@/utils/uploadUtils";
import { getDefaultLanguageData, valueByLanguage } from "@/utils/college";
import { mapGetters } from "vuex";

// 对齐galaxy：定义OSSApiKey常量
const OSSApiKey = {
    Common: "common",
    Message: "message"
};

export default {
    name: "BannerUploadData",
    components: {
        PushConfig,
        NavConfig
    },
    props: {
        moduleId: {
            type: Number,
            default: null
        },
        detailId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            loading: false,
            currentLang: "zh_CN",
            langTabs: [
                { label: "简体", value: "zh_CN" },
                { label: "繁体", value: "zh_TW" },
                { label: "英文", value: "en" }
            ],
            uploadImageAcceptFormat: ".png,.jpg,.bmp,.jpeg,.svg",
            // 临时文件：用于预览 + 上传
            tempFile: {
                zh_CN: null,
                zh_TW: null,
                en: null
            },
            formRules: {
                title: [
                    {
                        validator: (rule, value, callback) => {
                            const zhTitle = valueByLanguage(value, "zh_CN");
                            if (!zhTitle || !String(zhTitle.value || "").trim()) {
                                callback(new Error("请填写标题（简体）"));
                            } else {
                                callback();
                            }
                        },
                        trigger: "blur"
                    }
                ],
                fileAssetId: [
                    {
                        validator: (rule, value, callback) => {
                            // 新增时要求三种语言都必须有背景图
                            // 编辑时允许部分语言有背景图（但至少要有一种）
                            const requiredLangs = ["zh_CN", "zh_TW", "en"];
                            const hasAny = requiredLangs.some(
                                (lang) => this.hasLangTempFile(lang) || this.hasLangFileAsset(lang)
                            );
                            if (!hasAny) {
                                callback(new Error("请至少添加一种语言的背景图"));
                                return;
                            }
                            // 新增时要求三种语言完整
                            if (!this.detailId) {
                                const allComplete = requiredLangs.every(
                                    (lang) => this.hasLangTempFile(lang) || this.hasLangFileAsset(lang)
                                );
                                if (!allComplete) {
                                    callback(new Error("新增时请补全三种语言的背景图"));
                                    return;
                                }
                            }
                            callback();
                        },
                        trigger: "change"
                    }
                ]
            },
            formData: {
                moduleId: null,
                resourceType: "RESOURCE",
                title: getDefaultLanguageData(""),
                pushConditions: [],
                // 与 NavConfig.vue 枚举保持一致：0=NoNavigation
                navigationConfig: { navType: 0 },
                fileAssetId: getDefaultLanguageData("")
            }
        };
    },
    computed: {
        ...mapGetters(["bucketData"]), // 对齐galaxy：使用bucketData getter
        // 对齐 galaxy：直接返回当前语言的文件对象（可能为 null）
        currentFile() {
            return this.tempFile[this.currentLang] || null;
        }
    },
    watch: {
        moduleId: {
            immediate: true,
            handler(v) {
                this.formData.moduleId = v;
            }
        },
        detailId: {
            immediate: true,
            async handler(newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    await this.getDetail();
                } else if (!newVal && oldVal) {
                    this.resetFormData();
                }
            }
        }
    },
    mounted() {
        this.formData.moduleId = this.moduleId;
        if (this.detailId) {
            this.getDetail();
        }
    },
    beforeDestroy() {
        // 清理所有 blob URL
        Object.keys(this.tempFile).forEach((lang) => {
            const f = this.tempFile[lang];
            if (f && f.url && String(f.url).indexOf("blob:") === 0) {
                try {
                    URL.revokeObjectURL(f.url);
                } catch (e) {
                    // ignore
                }
            }
        });
    },
    methods: {
        // Vue2 template 里需要挂到实例上，才能在模板中调用
        valueByLanguage,
        hasLangFileAsset(lang) {
            const v = valueByLanguage(this.formData.fileAssetId, lang);
            return !!(v && v.value);
        },
        hasLangTempFile(lang) {
            const f = this.tempFile[lang];
            return !!(f && (f.file || f.url));
        },
        async getDetail() {
            const res = await api.getBannerMaterialDetails(this.detailId);
            const data = res && res.data ? res.data : {};
            // 只覆盖我们关心的字段
            this.formData.moduleId = data.moduleId || this.moduleId;
            this.formData.resourceType = data.resourceType || "RESOURCE";
            this.formData.title = data.title || getDefaultLanguageData("");
            this.formData.pushConditions = data.pushConditions || [];
            this.formData.navigationConfig = data.navigationConfig || { navType: 0 };
            this.formData.fileAssetId = data.fileAssetId || getDefaultLanguageData("");

            await this.initTempFile();
            if (this.$refs.pushConfigRef && this.formData.pushConditions.length) {
                this.$refs.pushConfigRef.initData(this.formData.pushConditions);
            }
            if (this.$refs.navConfigRef && this.formData.navigationConfig) {
                this.$refs.navConfigRef.initData(this.formData.navigationConfig);
            }
        },
        async initTempFile() {
            const ids = (this.formData.fileAssetId || [])
                .map((it) => Number(it.value))
                .filter(Boolean);
            if (!ids.length) return;
            const res = await api.getFileConfigById(ids);
            const items = res && res.data && res.data.items ? res.data.items : [];
            items.forEach((fileInfo) => {
                const langItem = (this.formData.fileAssetId || []).find(
                    (it) => Number(it.value) === fileInfo.id
                );
                if (!langItem) return;
                this.tempFile[langItem.locale] = {
                    url: fileInfo.url,
                    file: null,
                    data: {
                        name: fileInfo.name,
                        size: fileInfo.size,
                        md5: fileInfo.md5,
                        bucket: fileInfo.bucket,
                        path: fileInfo.path
                    }
                };
            });
        },
        handleFileChange(file) {
            const raw = file && file.raw ? file.raw : null;
            if (!raw) return;
            const url = URL.createObjectURL(raw);
            // 对齐galaxy：从store中动态获取bucket（不写死）
            const commonBucketData = this.bucketData && this.bucketData[OSSApiKey.Common];
            const bucket = (commonBucketData && commonBucketData.bucket) || "onyx-edu"; // 默认值
            // 对齐 galaxy：设置 tempFile，包含 file、url 和 data
            this.tempFile[this.currentLang] = {
                file: raw,
                url,
                data: {
                    name: raw.name,
                    size: raw.size,
                    md5: "",
                    bucket: bucket,
                    path: ""
                }
            };
            // 触发表单校验
            this.$nextTick(() => {
                if (this.$refs.formRef) {
                    this.$refs.formRef.validateField("fileAssetId");
                }
            });
        },
        removeCurrentFile() {
            const f = this.tempFile[this.currentLang];
            if (f && f.url && String(f.url).indexOf("blob:") === 0) {
                try {
                    URL.revokeObjectURL(f.url);
                } catch (e) {
                    // ignore
                }
            }
            this.tempFile[this.currentLang] = null;
            // 同步清空 assetId（编辑时删除文件）
            const target = valueByLanguage(this.formData.fileAssetId, this.currentLang);
            if (target) target.value = "";
            // 触发表单校验
            this.$nextTick(() => {
                if (this.$refs.formRef) {
                    this.$refs.formRef.validateField("fileAssetId");
                }
            });
        },
        resetFormData() {
            // 清理所有 blob URL
            Object.keys(this.tempFile).forEach((lang) => {
                const f = this.tempFile[lang];
                if (f && f.url && String(f.url).indexOf("blob:") === 0) {
                    try {
                        URL.revokeObjectURL(f.url);
                    } catch (e) {
                        // ignore
                    }
                }
            });
            // 重置表单数据
            this.tempFile = {
                zh_CN: null,
                zh_TW: null,
                en: null
            };
            this.formData = {
                moduleId: this.moduleId,
                resourceType: "RESOURCE",
                title: getDefaultLanguageData(""),
                pushConditions: [],
                navigationConfig: { navType: 0 },
                fileAssetId: getDefaultLanguageData("")
            };
            this.currentLang = "zh_CN";
            // 重置表单校验状态
            this.$nextTick(() => {
                if (this.$refs.formRef) {
                    this.$refs.formRef.clearValidate();
                }
                if (this.$refs.pushConfigRef) {
                    this.$refs.pushConfigRef.resetData && this.$refs.pushConfigRef.resetData();
                }
                if (this.$refs.navConfigRef) {
                    this.$refs.navConfigRef.resetData && this.$refs.navConfigRef.resetData();
                }
            });
        },
        async uploadFiles() {
            // 只上传有新文件的语言（有 file 属性的）
            const toUpload = Object.keys(this.tempFile)
                .map((lang) => ({ lang, item: this.tempFile[lang] }))
                .filter(({ item }) => item && item.file);
            if (!toUpload.length) {
                // 没有新文件需要上传，直接返回（编辑时可能只修改了其他字段）
                return;
            }

            // 对齐galaxy：从store中动态获取bucket（不写死）
            const commonBucketData = this.bucketData && this.bucketData[OSSApiKey.Common];
            const bucket = (commonBucketData && commonBucketData.bucket) || "onyx-edu"; // 默认值

            // 走 galaxy 同款：md5 -> batchCreateFiles -> oss upload -> 写回 fileAssetId
            const items = [];
            for (const { lang, item } of toUpload) {
                const md5 = await getMd5(item.file);
                items.push({
                    name: item.file.name,
                    folder: "res_banner/resource/",
                    size: item.file.size,
                    bucket: bucket, // 对齐galaxy：使用动态获取的bucket
                    md5
                });
            }

            const res = await api.batchCreateFiles({ items, headers: { "client-type": "boox" } });
            const created = res && res.data && res.data.items ? res.data.items : [];
            if (!created.length || created.length !== toUpload.length) {
                throw new Error("文件处理失败，请重试");
            }

            // 注意：按顺序对应
            for (let i = 0; i < toUpload.length; i++) {
                const { lang, item } = toUpload[i];
                const fileAsset = created[i];
                if (!fileAsset) continue;

                // 对齐galaxy：使用后端返回的bucket，如果没有则从store获取
                const uploadBucket = fileAsset.bucket || bucket;
                await uploadFileToOssByPath({
                    file: item.file,
                    path: fileAsset.path,
                    bucket: uploadBucket
                });

                const target = valueByLanguage(this.formData.fileAssetId, lang);
                if (target) {
                    target.value = String(fileAsset.id);
                }

                // 更新预览 url（编辑态/上传后）
                // 清理旧的 blob URL
                if (item.url && String(item.url).indexOf("blob:") === 0) {
                    try {
                        URL.revokeObjectURL(item.url);
                    } catch (e) {
                        // ignore
                    }
                }
                // 更新为服务器 URL
                this.tempFile[lang] = {
                    file: null,
                    url: fileAsset.url || "",
                    data: fileAsset
                };
            }
        },
        async handleSubmit() {
            try {
                this.loading = true;

                // 兜底：对齐 galaxy，moduleId 必须存在
                this.formData.moduleId = this.formData.moduleId || this.moduleId;
                if (!this.formData.moduleId) {
                    this.$message.error("模块ID缺失，请先选择模块");
                    return;
                }

                // 使用 Element UI 表单校验
                await new Promise((resolve, reject) => {
                    this.$refs.formRef.validate((valid) => {
                        if (valid) {
                            resolve();
                        } else {
                            reject(new Error("表单校验失败"));
                        }
                    });
                });

                // 上传新文件（会写回 fileAssetId）
                await this.uploadFiles();

                // 上传后再次校验背景图（新增时要求三种语言完整）
                if (!this.detailId) {
                    const requiredLangs = ["zh_CN", "zh_TW", "en"];
                    const allComplete = requiredLangs.every(
                        (lang) => this.hasLangFileAsset(lang)
                    );
                    if (!allComplete) {
                        this.$message.error("新增时请补全三种语言的背景图");
                        return;
                    }
                }

                // 取 push/nav 配置
                if (this.$refs.pushConfigRef) {
                    this.formData.pushConditions = this.$refs.pushConfigRef.getData() || [];
                }
                if (this.$refs.navConfigRef) {
                    this.formData.navigationConfig = this.$refs.navConfigRef.getData() || { navType: 0 };
                }

                // 提交数据
                if (this.detailId) {
                    await api.updateBannerMaterial(this.detailId, this.formData);
                } else {
                    await api.addBannerMaterial(this.formData);
                }
                this.$message.success("操作成功");
                this.$emit("success");
            } catch (e) {
                // 如果是表单校验错误，不显示额外的错误提示（Element UI 会自动显示）
                if (e && e.message === "表单校验失败") {
                    return;
                }
                // 其他错误显示具体错误信息
                const errorMsg = e && e.response && e.response.data && e.response.data.message
                    ? e.response.data.message
                    : (e && e.message ? e.message : "保存失败，请重试");
                this.$message.error(errorMsg);
                console.error("保存失败:", e);
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
.banner-upload {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.banner-form {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding-right: 8px;
}

.file-form-item {
    position: relative;
}

.file-label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.file-hint {
    color: #909399;
    font-size: 12px;
    margin-bottom: 8px;
}

.file-size {
    color: #f56c6c;
    font-size: 12px;
}

.file-lang-tabs {
    position: absolute;
    top: -34px;
    right: 8px;
    display: flex;
    gap: 10px;
}

.lang-tabs {
    display: flex;
    gap: 10px;
}

.lang-tab {
    cursor: pointer;
    font-size: 12px;
    color: #606266;
}

.lang-tab.active {
    color: #409eff;
    font-weight: 600;
}

.file-area {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    overflow: hidden;
    height: 100px;
    width: 100%;
}

/* 有数据时，file-area 宽度自适应 */
.file-area.file-area-has-content {
    width: auto;
    display: inline-block;
}

.file-empty {
    width: 100%;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.file-preview-wrapper {
    display: inline-block;
}

.file-preview-container {
    position: relative;
    height: 100px;
    overflow: hidden;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    display: inline-block;
}

.file-img {
    height: 100px;
    width: auto;
    display: block;
}

.file-img :deep(.el-image__inner) {
    height: 100px !important;
    width: auto !important;
    object-fit: contain !important;
}

.file-img :deep(.el-image) {
    height: 100px;
    width: auto;
    display: inline-block;
}

.file-preview-container .file-upload :deep(.el-upload) {
    width: auto;
    height: 100%;
    display: inline-block;
    border: none;
    padding: 0;
    margin: 0;
}

.file-remove {
    position: absolute;
    top: 0;
    right: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    border-bottom-left-radius: 12px;
    cursor: pointer;
}

.footer {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    gap: 10px;
    padding: 12px 0 0;
    border-top: 1px solid #ebeef5;
    background: #fff;
}

.file-upload :deep(.el-upload) {
    width: 100%;
    height: 100%;
    display: block;
    border: none;
}
</style>
